// Reusable Leaflet tile-layer wrapper with a Satellite / Map toggle.
//
// Usage inside any react-leaflet <MapContainer>:
//   <MapTileToggle
//     mapUrl="https://{s}.basemaps.cartocdn.com/voyager/{z}/{x}/{y}.png"
//     mapAttribution='&copy; OpenStreetMap &copy; CARTO'
//     mapSubdomains={['a','b','c','d']}
//     mapMaxZoom={20}
//   />
//
// Defaults to satellite (Esri World Imagery — free, no API key) and
// surfaces a small button in the top-right corner of the map that
// flips back and forth between the two layers. Preserves each map's
// existing "Map" style so the brand voice (light vs. voyager vs.
// minimal) survives untouched.
//
// Implementation notes:
//   • The Leaflet Control is created ONCE on mount (dep array is
//     [map, position] only). Previously the control was destroyed
//     and re-added on every `mode` change, which occasionally left
//     the DOM stale between the `remove()` and `addTo()` calls and
//     made the toggle label look like it wasn't updating.
//   • Mode changes now mutate the existing button's `textContent` +
//     `aria-*` in place — cheap, atomic, and free of remount races.
//   • Click handler + toggle callback are read through refs so the
//     always-current mode & onToggle are used without needing to
//     re-bind the DOM event listener.
import { useEffect, useRef, useMemo, useState } from "react";
import { TileLayer, useMap } from "react-leaflet";
import L from "leaflet";

const SATELLITE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const SATELLITE_ATTRIBUTION =
  'Tiles &copy; <a href="https://www.esri.com/" target="_blank" rel="noreferrer">Esri</a>';
const SATELLITE_MAX_ZOOM = 19;

// The button label always describes the OTHER view — i.e., what the
// user will see after they click. This "switch to X" affordance is
// the same convention Google Maps and Apple Maps use, so the tiny
// pill inside the map corner reads intuitively across desktop,
// tablet and mobile without extra iconography.
const labelFor = (mode) => (mode === "satellite" ? "Map" : "Satellite");

const applyModeToButton = (btn, mode) => {
  if (!btn) return;
  const label = labelFor(mode);
  if (btn.textContent !== label) btn.textContent = label;
  btn.setAttribute("aria-label", `Switch to ${label} view`);
  btn.setAttribute("aria-pressed", mode === "satellite" ? "true" : "false");
  btn.setAttribute("data-map-mode", mode);
};

const ToggleControl = ({ mode, onToggle, position = "topright" }) => {
  const map = useMap();
  const btnRef = useRef(null);
  // Refs let the DOM click handler read the LATEST mode + onToggle
  // without us having to tear down and rebuild the Leaflet control
  // every time either changes. That's what previously caused the
  // stale-label races the user observed.
  const modeRef = useRef(mode);
  const toggleRef = useRef(onToggle);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { toggleRef.current = onToggle; }, [onToggle]);

  // Create the Leaflet control ONCE per map/position pair.
  useEffect(() => {
    if (!map) return undefined;
    const ControlClass = L.Control.extend({
      onAdd() {
        const wrap = L.DomUtil.create("div", "leaflet-bar map-tile-toggle");
        const btn = L.DomUtil.create("button", "map-tile-toggle__btn", wrap);
        btn.type = "button";
        btn.setAttribute("data-testid", "map-tile-toggle");
        applyModeToButton(btn, modeRef.current);
        btnRef.current = btn;

        L.DomEvent.disableClickPropagation(wrap);
        L.DomEvent.disableScrollPropagation(wrap);
        L.DomEvent.on(btn, "click", (e) => {
          L.DomEvent.preventDefault(e);
          const fn = toggleRef.current;
          if (typeof fn === "function") fn();
        });
        return wrap;
      },
    });
    const control = new ControlClass({ position });
    control.addTo(map);
    return () => {
      btnRef.current = null;
      try { control.remove(); } catch { /* map already torn down */ }
    };
  }, [map, position]);

  // Mutate the existing button when the mode flips — no re-add of
  // the Leaflet control, no DOM race.
  useEffect(() => {
    applyModeToButton(btnRef.current, mode);
  }, [mode]);

  return null;
};

const MapTileToggle = ({
  mapUrl,
  mapAttribution = "",
  mapSubdomains,
  mapMaxZoom = 19,
  togglePosition = "topright",
  // Some maps (postcards, branding overlays) want the satellite
  // layer but no toggle UI — they can pass `noToggle` and just
  // inherit the satellite default.
  noToggle = false,
}) => {
  const [mode, setMode] = useState("satellite");
  const onToggle = useMemo(
    () => () => setMode((m) => (m === "satellite" ? "map" : "satellite")),
    []
  );
  const tileProps = mode === "satellite"
    ? { url: SATELLITE_URL, attribution: SATELLITE_ATTRIBUTION, maxZoom: SATELLITE_MAX_ZOOM }
    : {
        url: mapUrl,
        attribution: mapAttribution,
        maxZoom: mapMaxZoom,
        ...(mapSubdomains ? { subdomains: mapSubdomains } : {}),
      };

  return (
    <>
      <TileLayer key={mode} {...tileProps} />
      {noToggle ? null : (
        <ToggleControl
          mode={mode}
          onToggle={onToggle}
          position={togglePosition}
        />
      )}
    </>
  );
};

export default MapTileToggle;
